/**
 * ArticleEditorPage — mode-routing shell for the article editor
 * (article-canvas-flow, modules/articles/ARTICLE_CANVAS_FLOW_PLAN.md).
 *
 * Two hosting modes per article (draft.editorMode, default 'screen'):
 * - 'screen' — the classic fullscreen single-screen editor, unchanged except
 *   the article-gated `Screen | Flow` toggle in its header (headerAction seam).
 * - 'flow'   — article top bar (back · title · status · toggle · Publish · ⋮)
 *   over the embedded V2 canvas driven by ArticleV2ScreenFlowCoordinator.
 *
 * Forced-Flow guard (AC-009): a document with >1 screens always opens Flow;
 * the Screen toggle is disabled with a tooltip. Screen-mode saves keep the
 * legacy replaceLaunchStep path — only ever reachable for 1-screen docs.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ListAltIcon from '@mui/icons-material/ListAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PaymentIcon from '@mui/icons-material/Payment';
import { getAuth } from 'firebase/auth';
import type { FlowDocument, StepNode } from '@segmently/schema-models/v2';
import {
  ArticleDraftService,
  WebPlacementService,
  type ArticleDraft,
  type ArticleEditorMode,
} from '@modules/firebase-core';
import { OnboardingScreenEditorDialogV2 } from '@modules/reactFlow/components/onboarding/OnboardingScreenEditorDialogV2';
import { OnboardingCanvasEditorV2 } from '@modules/reactFlow/components/onboarding/OnboardingCanvasEditorV2';
import { ArticleV2ScreenFlowCoordinator } from '@modules/reactFlow/services/article/ArticleV2ScreenFlowCoordinator';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import { ArticleDetailsDialog } from '@modules/support-flow/components/ArticleDetailsDialog';
import { ArticleEditorModeToggle } from '@modules/support-flow/components/ArticleEditorModeToggle';
import { ArticlePublishApiClient } from '@modules/support-flow/services/ArticlePublishApiClient';
import { ArticleWebPublishService } from '@modules/support-flow/services/ArticleWebPublishService';
import { articleStatusChipProps } from '@modules/support-flow/utils/articleStatusChip';
import { getArticleTemplateScreens } from '@modules/support-flow/utils/articleTemplateScreens';
import { WebPlacementDialog, DeployWebPlacementDialog } from '@modules/webplacements';
import type {
  CreateWebPlacementInput,
  UpdateWebPlacementInput,
  WebPlacement,
  WebPlacementDeployResult,
} from '@modules/webplacements/types/webplacements.types';
import { useNotification } from '@frontend/contexts/NotificationContext';

export function ArticleEditorPage() {
  const { projectId, articleId } = useParams<{ projectId: string; articleId: string }>();
  const navigate = useNavigate();
  const notifications = useNotification();
  const articleService = useMemo(() => new ArticleDraftService(), []);
  const publishClient = useMemo(() => new ArticlePublishApiClient(), []);
  const webPublishService = useMemo(() => new ArticleWebPublishService(), []);
  const placementService = useMemo(() => new WebPlacementService(), []);

  const [draft, setDraft] = useState<ArticleDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mode override chosen in THIS session (in-place switch, no route change);
  // null → follow the persisted draft.editorMode.
  const [modeOverride, setModeOverride] = useState<ArticleEditorMode | null>(null);
  const [modeSwitching, setModeSwitching] = useState(false);

  // Live screen count while the canvas is open — the coordinator mutates the
  // document behind this shell's back; refreshed after every settled sync.
  const [liveScreenCount, setLiveScreenCount] = useState<number | null>(null);
  const [canvasSyncing, setCanvasSyncing] = useState(false);
  const coordinatorRef = useRef<ArticleV2ScreenFlowCoordinator | null>(null);

  const loadDraft = useCallback(async (): Promise<ArticleDraft | null> => {
    if (!projectId || !articleId) return null;
    const result = await articleService.getDraft(projectId, articleId);
    if (result) setDraft(result);
    return result;
  }, [articleService, articleId, projectId]);

  useEffect(() => {
    let mounted = true;

    async function initialLoad() {
      if (!projectId || !articleId) {
        setError('Project id and article id are required.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const result = await articleService.getDraft(projectId, articleId);
        if (!mounted) return;
        if (!result) {
          setError('Article draft was not found.');
          return;
        }
        setDraft(result);
      } catch (err) {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load article draft');
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void initialLoad();

    return () => {
      mounted = false;
    };
  }, [articleId, articleService, projectId]);

  // ------------------------------------------------------------------
  // Mode resolution (AC-007/008/009/022)
  // ------------------------------------------------------------------
  const draftScreenCount = Object.keys(draft?.flowDocument?.screens ?? {}).length;
  const screenCount = liveScreenCount ?? draftScreenCount;
  const forcedFlow = screenCount > 1;
  const effectiveMode: ArticleEditorMode = forcedFlow
    ? 'flow'
    : (modeOverride ?? draft?.editorMode ?? 'screen');

  const handleModeChange = useCallback(async (next: ArticleEditorMode) => {
    if (!projectId || !articleId || modeSwitching) return;
    setModeSwitching(true);
    try {
      // Fresh read first: Flow-mode canvas edits (screens added/removed) are
      // invisible to this shell until reloaded, and the Screen editor must
      // open on the CURRENT launch screen.
      const fresh = await loadDraft();
      const count = Object.keys(fresh?.flowDocument?.screens ?? {}).length;
      if (next === 'screen' && count > 1) {
        // Forced-Flow guard — the toggle is disabled in this state; this is
        // the belt-and-braces path for a stale enablement.
        return;
      }
      const updated = await articleService.updateDetails(projectId, articleId, { editorMode: next });
      setDraft(updated);
      setModeOverride(next);
      setLiveScreenCount(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to switch editor mode';
      notifications?.showNotification?.(message, 'error');
    } finally {
      setModeSwitching(false);
    }
  }, [articleId, articleService, loadDraft, modeSwitching, notifications, projectId]);

  // ------------------------------------------------------------------
  // Flow-mode canvas wiring
  // ------------------------------------------------------------------
  const coordinatorFactory = useCallback(() => {
    const coordinator = new ArticleV2ScreenFlowCoordinator(projectId ?? '', articleId ?? '');
    coordinatorRef.current = coordinator;
    return coordinator;
  }, [articleId, projectId]);

  const handleCanvasSyncingChange = useCallback((isSyncing: boolean) => {
    setCanvasSyncing(isSyncing);
    if (!isSyncing) {
      const count = coordinatorRef.current?.getAllEntities().length;
      if (typeof count === 'number' && count > 0) setLiveScreenCount(count);
    }
  }, []);

  const articleTemplates = useMemo<StepNode[]>(
    () => getArticleTemplateScreens(draft?.defaultLanguage || 'en'),
    [draft?.defaultLanguage],
  );

  const goToArticlesList = useCallback(() => {
    navigate(projectId ? `/project/${projectId}/articles` : '/all-projects');
  }, [navigate, projectId]);

  // ------------------------------------------------------------------
  // Screen-mode save (legacy single-screen path — only 1-screen docs get here)
  // ------------------------------------------------------------------
  const launchStep = useMemo(() => {
    if (!draft?.flowDocument?.launchScreenId) return null;
    const step = draft.flowDocument.screens?.[draft.flowDocument.launchScreenId];
    return step ? toFlowStepNode(step) : null;
  }, [draft]);

  const handleScreenModeSave = useCallback(async (step: FlowStepNode) => {
    if (!projectId || !articleId || !draft) return;
    const stepNode = toStepNode(step);
    const nextFlowDocument = replaceLaunchStep(draft.flowDocument, stepNode);
    const saved = await articleService.saveFlowDocument(projectId, articleId, nextFlowDocument);
    setDraft(saved);
    notifications?.showNotification?.('Article saved', 'success');
  }, [articleId, articleService, draft, notifications, projectId]);

  // ------------------------------------------------------------------
  // Publish — CDN (Flow bar primary; AC-010/023)
  // ------------------------------------------------------------------
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const isArticleAliasUnique = useCallback(
    (alias: string, excludeArticleId: string) => {
      if (!projectId) return Promise.resolve(false);
      return articleService.isAliasUnique(projectId, alias, excludeArticleId);
    },
    [articleService, projectId],
  );

  const openPublishDialog = useCallback(async () => {
    // Fresh read — the canvas saves behind this shell's back.
    await loadDraft();
    setPublishDialogOpen(true);
  }, [loadDraft]);

  const handlePublishConfirm = useCallback(async (input: { alias: string }) => {
    if (!projectId || !articleId || !draft) return;
    let draftToPublish = draft;
    if (input.alias !== draft.alias) {
      draftToPublish = await articleService.updateDetails(projectId, articleId, { alias: input.alias });
      setDraft(draftToPublish);
    }
    setPublishDialogOpen(false);
    setPublishing(true);
    setError(null);
    try {
      const publishResult = await publishClient.publish({
        projectId,
        alias: draftToPublish.alias,
        flowDocument: draftToPublish.flowDocument,
        locale: draftToPublish.defaultLanguage,
        title: draftToPublish.title,
      });
      const published = await articleService.markPublished(projectId, articleId, {
        url: publishResult.customDomainUrl || publishResult.gatewayUrl || publishResult.url,
        assetUrl: publishResult.url,
        configUrl: publishResult.configUrl,
        gatewayUrl: publishResult.gatewayUrl,
        customDomainUrl: publishResult.customDomainUrl,
        alias: publishResult.alias,
        webShellVersion: publishResult.webShellVersion,
      });
      setDraft(published);
      notifications?.showNotification?.('Article published', 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish article';
      notifications?.showNotification?.(message, 'error');
    } finally {
      setPublishing(false);
    }
  }, [articleId, articleService, draft, notifications, projectId, publishClient]);

  // ------------------------------------------------------------------
  // Publish with payments (⋮ menu → WebPlacementDialog; AC-011)
  // ------------------------------------------------------------------
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [paymentsDraft, setPaymentsDraft] = useState<ArticleDraft | null>(null);
  const [paymentsPlacement, setPaymentsPlacement] = useState<WebPlacement | null>(null);
  const [deployDraft, setDeployDraft] = useState<ArticleDraft | null>(null);
  const [deployPlacement, setDeployPlacement] = useState<WebPlacement | null>(null);

  const handlePublishWithPayments = useCallback(async () => {
    if (!projectId) return;
    try {
      const fresh = await loadDraft();
      if (!fresh) return;
      const existing = await placementService.getByAlias(projectId, fresh.alias);
      if (existing && existing.sourceKind !== 'article') {
        throw new Error(`Alias "${fresh.alias}" is already used by a web placement. Change the article alias.`);
      }
      setPaymentsPlacement(existing ?? null);
      setPaymentsDraft(fresh);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to open publish settings';
      notifications?.showNotification?.(message, 'error');
    }
  }, [loadDraft, notifications, placementService, projectId]);

  const closePaymentsDialog = useCallback(() => {
    setPaymentsDraft(null);
    setPaymentsPlacement(null);
  }, []);

  const handleArticlePlacementCreate = useCallback(async (data: Omit<CreateWebPlacementInput, 'createdBy'>) => {
    if (!projectId) throw new Error('Project id is required');
    return placementService.create(projectId, {
      ...data,
      sourceKind: 'article',
      createdBy: getAuth().currentUser?.email ?? 'unknown',
    });
  }, [placementService, projectId]);

  const handleArticlePlacementUpdate = useCallback(async (id: string, data: UpdateWebPlacementInput) => {
    if (!projectId) throw new Error('Project id is required');
    return placementService.update(projectId, id, data);
  }, [placementService, projectId]);

  const isPlacementAliasUnique = useCallback(async (alias: string, excludeId?: string) => {
    if (!projectId) return false;
    return placementService.isAliasUnique(projectId, alias, excludeId);
  }, [placementService, projectId]);

  const handleArticleDeploy = useCallback(async (placement: WebPlacement) => {
    const target = paymentsDraft;
    if (!projectId || !target) return;
    closePaymentsDialog();
    setDeployDraft(target);
    setDeployPlacement(placement);
  }, [closePaymentsDialog, paymentsDraft, projectId]);

  const closeDeployDialog = useCallback(() => {
    setDeployDraft(null);
    setDeployPlacement(null);
  }, []);

  const executeArticleWebDeploy = useCallback(async (): Promise<WebPlacementDeployResult> => {
    const target = deployDraft;
    const placement = deployPlacement;
    if (!projectId || !target || !placement) {
      return { success: false, webUrl: '', gcsFiles: [], error: 'Article draft is no longer available' };
    }
    try {
      const result = await webPublishService.publish({
        projectId,
        articleId: target.id,
        alias: target.alias,
        title: target.title,
        flowDocument: target.flowDocument,
        createdBy: getAuth().currentUser?.email ?? 'unknown',
      }, placement);
      if (!result.success) {
        return { success: false, webUrl: result.webUrl ?? '', gcsFiles: [], error: result.error || 'Failed to publish article to web' };
      }
      notifications?.showNotification?.('Article published to web (payments enabled)', 'success');
      return { success: true, webUrl: result.webUrl, gcsFiles: [] };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to publish article to web';
      return { success: false, webUrl: '', gcsFiles: [], error: message };
    }
  }, [deployDraft, deployPlacement, notifications, projectId, webPublishService]);

  // ------------------------------------------------------------------
  // Edit details (⋮ menu)
  // ------------------------------------------------------------------
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleDetailsConfirm = useCallback(async (input: { title?: string; alias: string; editorMode?: ArticleEditorMode }) => {
    if (!projectId || !articleId) return;
    const updated = await articleService.updateDetails(projectId, articleId, input);
    setDraft(updated);
    if (input.editorMode) setModeOverride(input.editorMode);
    setDetailsOpen(false);
    notifications?.showNotification?.('Article details updated', 'success');
  }, [articleId, articleService, notifications, projectId]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">Loading article editor</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !projectId || !articleId || !draft) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Article editor could not be opened.'}</Alert>
      </Box>
    );
  }

  const modeToggle = (
    <ArticleEditorModeToggle
      mode={effectiveMode}
      screenModeDisabled={forcedFlow}
      disabled={modeSwitching}
      onChange={handleModeChange}
    />
  );

  if (effectiveMode === 'screen') {
    if (!launchStep) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Article launch screen was not found.</Alert>
        </Box>
      );
    }
    return (
      <OnboardingScreenEditorDialogV2
        open={true}
        onClose={goToArticlesList}
        stepData={launchStep}
        onStepSaved={handleScreenModeSave}
        projectId={projectId}
        defaultLanguage={draft.defaultLanguage || draft.flowDocument.defaultLanguage || 'en'}
        languages={draft.flowDocument.languages?.length ? draft.flowDocument.languages : ['en']}
        flowDocument={draft.flowDocument}
        mode="full"
        initialDevice="desktop"
        headerAction={modeToggle}
      />
    );
  }

  const statusChip = publishing ? (
    <Chip
      size="small"
      variant="outlined"
      color="primary"
      icon={<CircularProgress size={12} color="inherit" />}
      label="Publishing…"
      data-testid="article-publishing-chip"
    />
  ) : (
    <Chip
      size="small"
      {...articleStatusChipProps(draft.status)}
      data-testid="article-flow-status-chip"
    />
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Article Flow top bar — the ONLY publish surface in the editor. */}
      <Box
        data-testid="article-flow-topbar"
        sx={{
          height: 56,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <IconButton size="small" onClick={goToArticlesList} aria-label="Back to articles" data-testid="article-flow-back">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, maxWidth: 420 }}>
          {draft.title}
        </Typography>
        {statusChip}
        <Box sx={{ flex: 1 }} />
        {modeToggle}
        <Button
          variant="contained"
          size="small"
          onClick={openPublishDialog}
          disabled={publishing || canvasSyncing}
          data-testid="article-flow-publish"
        >
          Publish
        </Button>
        <IconButton
          size="small"
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          aria-label="Article actions"
          data-testid="article-flow-menu"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            data-testid="article-flow-menu-publish-web"
            onClick={() => {
              setMenuAnchor(null);
              void handlePublishWithPayments();
            }}
          >
            <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Publish with payments (web)…</ListItemText>
          </MenuItem>
          <MenuItem
            data-testid="article-flow-menu-edit-details"
            onClick={() => {
              setMenuAnchor(null);
              setDetailsOpen(true);
            }}
          >
            <ListItemIcon><DriveFileRenameOutlineIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit details…</ListItemText>
          </MenuItem>
          <MenuItem
            data-testid="article-flow-menu-articles-list"
            onClick={() => {
              setMenuAnchor(null);
              goToArticlesList();
            }}
          >
            <ListItemIcon><ListAltIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Articles list</ListItemText>
          </MenuItem>
        </Menu>
      </Box>

      {/* Embedded V2 canvas over the article draft. */}
      <Box sx={{ flex: 1, minHeight: 0 }} data-testid="article-flow-canvas">
        <OnboardingCanvasEditorV2
          open
          embedded
          hideTopPanel
          hideLocaleTools
          onClose={goToArticlesList}
          projectId={projectId}
          onboardingId=""
          versionId=""
          onboardingName={draft.title}
          hostKind="article"
          coordinatorFactory={coordinatorFactory}
          templates={articleTemplates}
          defaultLanguage={draft.defaultLanguage || draft.flowDocument.defaultLanguage || 'en'}
          languages={draft.flowDocument.languages?.length ? [...draft.flowDocument.languages] : ['en']}
          platform="web"
          onSyncingChange={handleCanvasSyncingChange}
        />
      </Box>

      {/* CDN publish confirm — alias is the public URL (same dialog as the list). */}
      <ArticleDetailsDialog
        open={publishDialogOpen}
        mode="publish"
        draft={draft}
        onClose={() => setPublishDialogOpen(false)}
        isAliasUnique={isArticleAliasUnique}
        onConfirm={handlePublishConfirm}
        busy={publishing}
      />

      {/* Edit details (title + alias + editor mode). */}
      <ArticleDetailsDialog
        open={detailsOpen}
        mode="edit"
        draft={draft}
        onClose={() => setDetailsOpen(false)}
        isAliasUnique={isArticleAliasUnique}
        onConfirm={handleDetailsConfirm}
        showEditorMode
        editorModeLocked={forcedFlow}
      />

      {/* Placement settings before EVERY web publish (sourceKind:'article'). */}
      {paymentsDraft && (
        <WebPlacementDialog
          open
          onClose={closePaymentsDialog}
          onCreate={handleArticlePlacementCreate}
          onUpdate={handleArticlePlacementUpdate}
          onDeployRequested={handleArticleDeploy}
          projectId={projectId}
          userId={getAuth().currentUser?.email ?? 'unknown'}
          mode={paymentsPlacement ? 'edit' : 'create'}
          webPlacement={paymentsPlacement ?? undefined}
          isAliasUnique={isPlacementAliasUnique}
          lockOnboarding
          initialOnboardingSelection={{
            onboardingId: paymentsDraft.id,
            onboardingName: paymentsDraft.title || paymentsDraft.alias,
            versionId: 'article-draft',
            versionName: 'Article draft',
            versionCreatedAt: paymentsDraft.updatedAt,
          }}
          articleFlowDocument={paymentsDraft.flowDocument}
        />
      )}

      {/* Publish progress + final URL — same dialog as onboarding placements. */}
      <DeployWebPlacementDialog
        open={Boolean(deployPlacement)}
        onClose={closeDeployDialog}
        webPlacement={deployPlacement}
        onDeploy={executeArticleWebDeploy}
        projectId={projectId}
      />
    </Box>
  );
}

function toFlowStepNode(step: StepNode): FlowStepNode {
  return {
    ...step,
    entityId: step.id,
  };
}

function toStepNode(step: FlowStepNode): StepNode {
  const { entityId, ...stepNode } = step as FlowStepNode & { entityId?: string };
  return stepNode as StepNode;
}

function replaceLaunchStep(flowDocument: FlowDocument, step: StepNode): FlowDocument {
  const screens = { ...(flowDocument.screens ?? {}) };
  if (flowDocument.launchScreenId && flowDocument.launchScreenId !== step.id) {
    delete screens[flowDocument.launchScreenId];
  }
  screens[step.id] = step;

  return {
    ...flowDocument,
    launchScreenId: step.id,
    screens,
  };
}

export default ArticleEditorPage;
